import React, { useState, useEffect } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './SellerStats.module.css';

/**
 * SellerStats — stats panel for sellers.
 *
 * Shows listings breakdown, transaction revenue, and review average.
 * Fetches from /api/seller-dashboard/stats.
 *
 * Usage (in ProfilePage or a dedicated SellerDashboardPage):
 *   {features.sellerDashboard && <SellerStats />}
 */
const SellerStats = ({ className }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch('/api/seller-dashboard/stats')
      .then(r => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <div className={css.loading}><FormattedMessage id="SellerStats.loading" /></div>;
  if (!stats) return null;

  const { listings, transactions, reviews } = stats;
  const revenueFormatted = (transactions.revenue.amount / 100).toFixed(2);

  return (
    <div className={classNames(css.root, className)}>
      <h2 className={css.title}><FormattedMessage id="SellerStats.title" /></h2>
      <div className={css.grid}>
        <div className={css.card}>
          <div className={css.value}>{listings.published}</div>
          <div className={css.label}><FormattedMessage id="SellerStats.published" /></div>
        </div>
        <div className={css.card}>
          <div className={css.value}>{listings.total}</div>
          <div className={css.label}><FormattedMessage id="SellerStats.totalListings" /></div>
        </div>
        <div className={css.card}>
          <div className={css.value}>{transactions.completed}</div>
          <div className={css.label}><FormattedMessage id="SellerStats.completed" /></div>
        </div>
        <div className={css.card}>
          <div className={css.value}>
            {revenueFormatted} {transactions.revenue.currency}
          </div>
          <div className={css.label}><FormattedMessage id="SellerStats.revenue" /></div>
        </div>
        {reviews.count > 0 && (
          <div className={css.card}>
            <div className={css.value}>⭐ {reviews.average}</div>
            <div className={css.label}>
              <FormattedMessage id="SellerStats.rating" values={{ count: reviews.count }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SellerStats.defaultProps = { className: null };
SellerStats.propTypes = { className: string };

export default SellerStats;
